/**
 * LOCAL SIMULATION DEFAULTS (labelled fixtures) mirrored from contracts/script/DefaultConfig.sol.
 * These are development/simulation candidates, not approved production economics (change order 4.0).
 */
import { keccak256, toHex, type Hex } from 'viem';
import {
  EncounterKind,
  type FormationTemplate,
  type HandlerParams,
  type MovementParams,
  type RoadParams,
  type WaterfallPolicy,
} from './types.js';

export const DEFAULTS = {
  supply: 1_000_000_000n * 10n ** 18n,
  hookFeeBps: 50n,
  lpFee: 3000n,
  tickSpacing: 60n,
  /** 8 s windows: a 3,610-unit course at pace 150 resolves in ~24 steps ≈ 3.2 minutes (playtest hypothesis). */
  windowSeconds: 8n,
  epochSeconds: 120n,
  maxDeferralSeconds: 1800n,
  previewWindows: 2n,
  /** Identity manifest fixture (not an approved manifest). */
  identityBacking: 100_000n * 10n ** 18n,
  identityCap: 1000n,
  /** Governance: proposed production minimum notice 24 h; local fixture 60 s. */
  govMinDelayProposed: 86_400n,
  govMinDelayLocal: 60n,
} as const;

/** keccak256("RUNNER identity trait seed - local fixture") — DefaultConfig.IDENTITY_TRAIT_SEED. */
export const IDENTITY_TRAIT_SEED: Hex = keccak256(toHex('RUNNER identity trait seed - local fixture'));


export function movementParams(): MovementParams {
  return {
    basePace: 150n,
    paceBonusMax: 30n,
    paceBonusVolumePpm: 2000n,
    xMax: 1000n,
    halfWidth: 40n,
    uMax: 300n,
    sensitivity: 1n,
    decayPpm: 500_000n,
    vxMax: 220n,
    forkMedian: 140n,
    stumbleDistance: 200n,
  };
}

export function handlerParams(quoteDecimals = 18): HandlerParams {
  const unit = 10n ** BigInt(quoteDecimals);
  return {
    barrierSlowPct: 30n,
    barrierSlowSteps: 2n,
    overheatSlowPct: 20n,
    overheatSteps: 3n,
    shoePrize: unit / 2n,
    shoeMinPrize: unit / 10n,
    forgeGateMax: 10n * unit,
    forgeAuthSeconds: 3600n,
    finishReleasesDeferred: true,
  };
}

/** Road budget fixture: up to 2 quote per run (min 0.2), 0.05 provision; Water 25 %; Overheat 10 %/step × 3, cap 30 %. */
export function roadParams(quoteDecimals = 18): RoadParams {
  const unit = 10n ** BigInt(quoteDecimals);
  return {
    runBudget: 2n * unit,
    minRunBudget: unit / 5n,
    opsProvision: unit / 20n,
    waterSliceBps: 2500n,
    overheatSliceBps: 1000n,
    overheatBiasSteps: 3n,
    overheatCap: (2n * unit * 3n) / 10n,
  };
}

/** WATERFALL_V1: holders 30 / base Forge 20 / road 20 / prize 10 / operations 20 (bps of R); 30 % of holders deferred. */
export function waterfallPolicy(): WaterfallPolicy {
  return { holdersBps: 3000n, forgeBps: 2000n, roadBps: 2000n, prizeBps: 1000n, opsBps: 2000n, holderDeferredBps: 3000n };
}

const obj = (kind: number, y: bigint, xL: bigint, xR: bigint) => ({ kind, y, xL, xR, param: 0n });

/** Formation 1 — "Clear fork": left branch Forge (with Forge gate), right branch Harvest. */
export function clearFork(): FormationTemplate {
  return {
    length: 1200n,
    mirrorable: false,
    maxShift: 0n,
    retired: false,
    objects: [
      obj(EncounterKind.FORK, 240n, -1000n, 1000n),
      obj(EncounterKind.FORGE_GATE, 600n, -1000n, -140n),
      obj(EncounterKind.MERGE, 1000n, -1000n, 1000n),
    ],
  };
}

/** Formation 2 — "Golden Shoe behind a barrier". */
export function shoeBehindBarrier(): FormationTemplate {
  return {
    length: 900n,
    mirrorable: true,
    maxShift: 200n,
    retired: false,
    objects: [obj(EncounterKind.BARRIER, 300n, -220n, 220n), obj(EncounterKind.GOLDEN_SHOE, 480n, -120n, 120n)],
  };
}

/**
 * Launch replacement for formation 2 — "Golden Shoe beside a barrier": the barrier covers one side and the shoe sits
 * just inside its edge, so ordinary play collects it ~30–41 % of the time (today's formation: ≤ 2 %) while steering
 * still helps (57–88 %). Same length, mirroring, shift and y positions as shoeBehindBarrier. Not part of the fixture
 * (defaultFormations is unchanged); selected by the launch profile. Evidence: packages/economics/data/shoe-formation.json.
 */
export function shoeBesideBarrier(): FormationTemplate {
  return {
    length: 900n,
    mirrorable: true,
    maxShift: 200n,
    retired: false,
    objects: [obj(EncounterKind.BARRIER, 300n, -520n, -140n), obj(EncounterKind.GOLDEN_SHOE, 480n, -160n, 80n)],
  };
}

/** Formation 3 — "Burning barricade, then water". */
export function barricadeThenWater(): FormationTemplate {
  return {
    length: 1000n,
    mirrorable: true,
    maxShift: 100n,
    retired: false,
    objects: [obj(EncounterKind.BURNING_BARRICADE, 300n, -1000n, 200n), obj(EncounterKind.WATER_STATION, 650n, -500n, -100n)],
  };
}

/** Engine v2 example formation — "Puddle lane". Valid only under engine v2+. */
export function puddleLane(): FormationTemplate {
  return {
    length: 900n,
    mirrorable: true,
    maxShift: 200n,
    retired: false,
    objects: [
      obj(EncounterKind.PUDDLE, 200n, -600n, -100n),
      obj(EncounterKind.BARRIER, 450n, 100n, 500n),
      obj(EncounterKind.PUDDLE, 700n, -200n, 300n),
    ],
  };
}

export function defaultFormations(): FormationTemplate[] {
  return [clearFork(), shoeBehindBarrier(), barricadeThenWater()];
}

/** Formation set of the launch profile: the fixture set with the collectible shoe formation in place of formation 2. */
export function launchFormations(): FormationTemplate[] {
  return [clearFork(), shoeBesideBarrier(), barricadeThenWater()];
}

/** Engine releases known to the reference engine: version → rules and the kinds it may place. */
export const ENGINES = {
  1: { rules: 1, kinds: [1, 2, 3, 4, 5, 6, 7, 8] },
  2: { rules: 2, kinds: [1, 2, 3, 4, 5, 6, 7, 8, 9] },
} as const;
